import { FC } from 'react'
import { Button, Row, Col, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { PlayCircleOutlined, RightOutlined } from '@ant-design/icons'
import './home-page.css'

const { Title, Paragraph } = Typography

interface HomePageProps {
  onStartLearning: () => void
}

const HomePage: FC<HomePageProps> = ({ onStartLearning }) => {
  const { t, i18n } = useTranslation(['pages'])

  const featureSteps = t('pages:features.steps', { returnObjects: true }) as Array<{
    step: string
    title: string
    subtitle: string
    desc: string
    features: string[]
  }>

  const stats = t('pages:why.stats', { returnObjects: true }) as Record<string, string>
  const highlights = t('pages:why.highlights', { returnObjects: true }) as Array<{
    icon: string
    title: string
    desc: string
    detail: string
  }>
  const reviews = t('pages:reviews.items', { returnObjects: true }) as Array<{
    name: string
    review: string
  }>

  return (
    <>
      <section
        id="hero"
        className="hero-section"
      >
        <div className="shell-container">
          <Row align="middle" gutter={[48, 32]}>
            <Col xs={24} lg={14}>
              <div className="hero-content">
                <h1
                  className="hero-title"
                  dangerouslySetInnerHTML={{ __html: t('pages:hero.title') }}
                />
                <Paragraph className="hero-subtitle">
                  {t('pages:hero.subtitle')}
                </Paragraph>
                <Button
                  type="primary"
                  size="large"
                  icon={<PlayCircleOutlined />}
                  className="hero-cta-button"
                  onClick={onStartLearning}
                >
                  {t('pages:hero.cta')} <RightOutlined />
                </Button>
              </div>
            </Col>
            <Col xs={24} lg={10}>
              <div
                className="hero-image-container"
              >
                <img src="/images/panda.png" alt="WellChinese AI Learning" className="hero-image" />
              </div>
            </Col>
          </Row>
        </div>
      </section>

      <section id="features" className="home-section home-section-white">
        <div className="shell-container">
          <div className="section-heading">
            <Title level={2} className="section-title">
              {t('pages:features.title')}
            </Title>
            <Paragraph className="section-subtitle narrow">
              {t('pages:features.subtitle')}
            </Paragraph>
          </div>

          <div className="feature-flow">
            <Row gutter={[48, 48]} align="middle">
              {featureSteps.map((step, i) => (
                <Col xs={24} sm={12} lg={6} key={i}>
                  <div className="feature-step">
                    <div
                      className="feature-step-badge"
                      style={{
                        width: '80px',
                        height: '80px',
                        background: `linear-gradient(135deg, ${i === 0 ? '#2563eb' : i === 1 ? '#3b82f6' : i === 2 ? '#60a5fa' : '#93c5fd'} 0%, ${i === 0 ? '#1d4ed8' : i === 1 ? '#2563eb' : i === 2 ? '#3b82f6' : '#60a5fa'} 100%)`,
                      }}
                    >
                      <span className="feature-step-number">{step.step}</span>
                    </div>

                    <div className="feature-card">
                      <Title level={3} className="feature-card-title">
                        {step.title}
                      </Title>
                      <Paragraph className="feature-card-subtitle">
                        {step.subtitle}
                      </Paragraph>
                      <Paragraph className="feature-card-desc">
                        {step.desc}
                      </Paragraph>

                      <div className="feature-chip-group">
                        {step.features.map((feature, fi) => (
                          <span key={fi} className="feature-chip">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </div>
      </section>

      <section
        id="why"
        className="home-section why-section"
      >
        <div className="why-overlay" />

        <div className="shell-container why-content">
          <div className="reviews-heading">
            <Title level={2} className="section-title" style={{ marginBottom: '16px' }}>
              {t('pages:why.title')}
            </Title>
            <Paragraph className="section-subtitle wide">
              {t('pages:why.subtitle')}
            </Paragraph>

            <Row gutter={[48, 24]} justify="center" className="stats-row">
              <Col xs={8} sm={8}>
                <div className="stat-block">
                  <div className="stat-number">
                    {stats.users}
                  </div>
                  <div className="stat-label">
                    {i18n.language === 'zh' ? '收录汉字' : 'Characters'}
                  </div>
                </div>
              </Col>
              <Col xs={8} sm={8}>
                <div className="stat-block">
                  <div className="stat-number">
                    {stats.improvement}
                  </div>
                  <div className="stat-label">
                    {i18n.language === 'zh' ? '收录词汇' : 'Vocabulary'}
                  </div>
                </div>
              </Col>
              <Col xs={8} sm={8}>
                <div className="stat-block">
                  <div className="stat-number">
                    {stats.satisfaction}
                  </div>
                  <div className="stat-label">
                    {i18n.language === 'zh' ? '收录成语' : 'Idioms'}
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          <Row gutter={[32, 32]} className="mobile-scroll-container highlights-row">
            {highlights.map((highlight, index) => (
              <Col xs={24} md={8} key={index}>
                <div className="highlight-card">
                  <div className="highlight-icon">{highlight.icon}</div>
                  <Title level={4} className="highlight-title">
                    {highlight.title}
                  </Title>
                  <Paragraph className="highlight-desc">
                    {highlight.desc}
                  </Paragraph>
                  <Paragraph className="highlight-detail">
                    {highlight.detail}
                  </Paragraph>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      <section id="reviews" className="home-section home-section-white reviews-section">
        <div className="shell-container">
          <div className="reviews-heading">
            <Title level={2} className="section-title">
              {t('pages:reviews.title')}
            </Title>
            <Paragraph className="reviews-subtitle">
              {t('pages:reviews.subtitle')}
            </Paragraph>
          </div>
          <div className="reviews-marquee">
            <div className="reviews-fade-left" />
            <div className="reviews-fade-right" />
            <div className="reviews-track">
              {[...Array(3)].map((_, setIndex) =>
                reviews.map((review, index) => (
                  <div key={`${setIndex}-${index}`} className="review-card">
                    <div className="review-stars">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className="review-star">
                          ★
                        </span>
                      ))}
                    </div>
                    <Paragraph className="review-text">
                      &ldquo;{review.review}&rdquo;
                    </Paragraph>
                    <div className="review-author">
                      {review.name}
                    </div>
                  </div>
                )),
              )}
            </div>
          </div>
        </div>
      </section>

      <section id="cta" className="home-section cta-section">
        <div className="shell-container">
          <div className="cta-content">
            <Title level={2} className="cta-title">
              {t('pages:cta.title')}
            </Title>
            <Paragraph className="cta-subtitle">
              {t('pages:cta.subtitle')}
            </Paragraph>
            <Button
              type="default"
              size="large"
              className="cta-button"
              onClick={onStartLearning}
            >
              {t('pages:cta.button')}
            </Button>
          </div>
        </div>
      </section>
    </>
  )
}

export default HomePage
